import React from "react";
import {
  Atom,
  Layers,
  FlaskConical,
  Cpu,
  Factory,
  Crown,
  BatteryCharging,
} from "lucide-react";
import SiteShell from "../../components/SiteShell";
import SEO from "../../components/SEO";
import { DeptHero, DeptSection, PersonCard, CardGrid } from "./_shared";

/**
 * /team/battery-rnd — Advanced Battery R&D team.
 *
 * Every member's work ladders to the GREEN axis: LFP now, sodium-ion
 * next on the same machinery, hard-carbon anode grown from Tamil Nadu
 * and Kerala coconut shells, feeding a 720 MWh/yr SIPCOT line.
 */

const materials = [
  {
    name: "Ushasri Gopalan",
    role: "Battery Materials Scientist",
    responsibility:
      "Synthesises cathode and anode active materials — LFP today, sodium-cathode variants on deck, both paired with hard-carbon from coconut shells.",
  },
  {
    name: "Ananth K",
    role: "Electrochemical Scientist",
    responsibility:
      "Runs the impedance and cyclic-voltammetry bench — every interface change validated before it ever touches a full cell.",
  },
  {
    name: "Vignesh Acharya",
    role: "Computational Materials Scientist",
    responsibility:
      "Screens the sodium-ion cathode phase space in silico — the DFT bench that tells the wet lab which recipes are worth pouring.",
  },
];

const cellDesign = [
  {
    name: "Saketh R",
    role: "Cell Design Engineer",
    responsibility:
      "Balances cell geometry, active-to-inactive ratio, and current-collector thickness for the LFP and Na-ion formats the SIPCOT line will build.",
  },
  {
    name: "Sandeep S",
    role: "Prototyping Engineer",
    responsibility:
      "Operates the pilot coating-stack-format line — every recipe change lands on real cells within days, not months.",
  },
  {
    name: "Himanshu K",
    role: "Electrode Process Engineer",
    responsibility:
      "Owns the slurry rheology and coating physics that decide whether a chemistry survives the jump from lab to gigafactory.",
  },
];

const testing = [
  {
    name: "Suresh S",
    role: "Battery Test Engineer",
    responsibility:
      "Cycles every chemistry through rate, thermal, and abuse profiles — the raw data that becomes cycle-life and warranty numbers.",
  },
  {
    name: "Anamika Sukumar",
    role: "Reliability / Lifetime Engineer",
    responsibility:
      "Models degradation across use profiles so a cell rated 6000 cycles ships with a 6000-cycle warranty, not a marketing claim.",
  },
  {
    name: "Sachi C",
    role: "Battery Data Scientist",
    responsibility:
      "Turns test-cycler data streams into failure-prediction models — early warnings that catch a bad cell before it reaches a customer pack.",
  },
  {
    name: "Lakshman V",
    role: "Safety & Abuse Engineer",
    responsibility:
      "Pushes cells past nail-penetration, overcharge, and thermal-runaway limits — the destructive proof that the 720 MWh product is safe.",
  },
];

const bms = [
  {
    name: "Sravanan A",
    role: "BMS Software / Firmware Engineer",
    responsibility:
      "Writes the SoC, SoH, and cell-balancing algorithms — the Indian replacement for imported BMS firmware inside every deployed pack.",
  },
  {
    name: "Kalidas N",
    role: "BMS Hardware Engineer",
    responsibility:
      "Designs the BMS sensor and comms hardware that turns a cell cluster into a monitored, dispatchable asset for griddata.",
  },
  {
    name: "Lankesh G",
    role: "Thermal Management Engineer",
    responsibility:
      "Keeps every pack in its optimal thermal window — from 45 °C Coimbatore summers to the coldest DISCOM substation container.",
  },
  {
    name: "Yogesh R",
    role: "Mechanical Design Engineer",
    responsibility:
      "Designs the pack enclosures — vibration, crash, and IP-rated for the utility-scale and containerised BESS families.",
  },
];

const scaleUp = [
  {
    name: "Ravindar Kumar",
    role: "Scale-Up / Chemical Engineer",
    responsibility:
      "Translates bench-scale slurry chemistry into ton-scale batch runs for the 720 MWh/yr line at SIPCOT.",
  },
  {
    name: "Kusum Kariyappa",
    role: "Quality Engineer",
    responsibility:
      "Sets X-ray, vision, and dimensional-inspection thresholds — every cell that leaves SIPCOT carries a serialised QA record.",
  },
];

const leadership = [
  {
    name: "Nagarjun",
    role: "Director of Battery R&D",
    responsibility:
      "Sets the cell roadmap — LFP now, sodium-ion next on the same machinery, hard-carbon anode grown from Tamil Nadu and Kerala coconut shells.",
  },
  {
    name: "Sudhakar H",
    role: "Technical Project Manager",
    responsibility:
      "Sequences chemistry, design, test, and scale-up so the 720 MWh line ramps without a single blocking dependency.",
  },
  {
    name: "Radhakrishnan S",
    role: "IP / Patent Counsel",
    responsibility:
      "Protects the non-Chinese cell-to-pack chain — every novel process route around coconut-shell hard-carbon files before it ships.",
  },
];

export default function BatteryRnD() {
  return (
    <SiteShell>
      <SEO
        title="Battery R&D Team — Green Gold Energy"
        description="Cell chemistry, BMS, pack design — the science under the 720 MWh factory at SIPCOT."
      />
      <DeptHero
        kicker="Advanced Battery R&D"
        title="Cell chemistry and pack engineering"
        sub="Cell chemistry, BMS, and pack design — the science under the 720 MWh factory. LFP now, sodium-ion next on the same machinery, both paired with hard-carbon anode grown from Tamil Nadu and Kerala coconut shells."
        Icon={BatteryCharging}
      />

      <DeptSection
        Icon={Crown}
        title="Leadership & Support"
        description="Strategic direction, program coordination and IP stewardship that keep the entire R&D engine moving forward."
      >
        <CardGrid minCol={340}>
          {leadership.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Atom}
        title="Materials & Fundamental Research"
        description="Pioneering the chemistry of tomorrow's cells — synthesis, electrochemistry and computational modeling of cathodes, anodes and electrolytes."
      >
        <CardGrid minCol={340}>
          {materials.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Layers}
        title="Cell Design & Prototyping"
        description="Translating chemistry into physical cells through precise engineering of geometry, electrode coatings and pilot-line prototypes."
      >
        <CardGrid minCol={340}>
          {cellDesign.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={FlaskConical}
        title="Testing, Validation & Analytics"
        description="Rigorous performance, reliability, safety and data-driven analytics to ensure every cell meets real-world demands."
      >
        <CardGrid minCol={340}>
          {testing.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Cpu}
        title="BMS & Pack Systems"
        description="Software, hardware, thermal and mechanical engineering that transforms individual cells into intelligent, safe battery packs."
      >
        <CardGrid minCol={340}>
          {bms.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Factory}
        title="Scale-Up & Manufacturing"
        description="Bench-scale chemistry translated to ton-scale batches with the quality thresholds a 720 MWh/yr line demands."
      >
        <CardGrid minCol={340}>
          {scaleUp.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <div style={{ height: 60 }} />
    </SiteShell>
  );
}
